"use client";

import { useState, useEffect } from "react";
import { api, type RelatoreWithCountDto } from "@/lib/api";
import { ScrollReveal } from "@/components/ScrollReveal";

interface SpeakersCloudBlockProps {
  title: string;
  /** "full" (default) = layout storico; "compact" = card con scroll lento */
  variant?: "full" | "compact";
  /** Solo compact: altezza massima del contenitore scrollante */
  compactMaxHeight?: string;
  /** Solo compact: mostra link "Vedi tutti" */
  showArchiveLink?: boolean;
  /** Solo compact: URL archivio (default "/eventi") */
  archiveLinkUrl?: string;
  className?: string;
}

function hashColor(slug: string): string {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = slug.charCodeAt(i) + ((hash << 5) - hash);
  }
  const r = Math.abs(hash >> 16) % 0x89;
  const g = Math.abs(hash >> 8) % 0x89;
  const b = Math.abs(hash) % 0x89;
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

export function SpeakersCloudBlock({
  title,
  variant = "full",
  compactMaxHeight = "20rem",
  showArchiveLink = true,
  archiveLinkUrl = "/eventi",
  className,
}: SpeakersCloudBlockProps) {
  const [relatori, setRelatori] = useState<RelatoreWithCountDto[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await api.relatori.withEventCount();
        if (!cancelled) setRelatori(data);
      } catch {
        if (!cancelled) setRelatori([]);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (relatori === null) {
    return (
      <section className={`max-w-7xl mx-auto px-4 sm:px-8 py-16 ${className || ""}`}>
        <div className="h-8 w-56 bg-neutral-200 rounded animate-pulse mb-8" />
        <div className="h-32 bg-neutral-100 rounded-xl animate-pulse" />
      </section>
    );
  }

  if (relatori.length === 0) return null;

  const sorted = [...relatori].sort((a, b) => {
    const aLast = a.nome.trim().split(/\s+/).pop() || "";
    const bLast = b.nome.trim().split(/\s+/).pop() || "";
    return aLast.localeCompare(bLast, "it");
  });

  /* ───────────── VARIANTE COMPACT ───────────── */
  if (variant === "compact") {
    return <SpeakersCloudCompact
      title={title}
      sorted={sorted}
      maxHeight={compactMaxHeight}
      showArchiveLink={showArchiveLink}
      archiveLinkUrl={archiveLinkUrl}
      className={className}
    />;
  }

  /* ───────────── VARIANTE FULL (pregresso) ───────────── */
  return (
    <section className={`max-w-7xl mx-auto px-4 sm:px-8 py-16 ${className || ""}`}>
      <ScrollReveal>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1a3a5c] uppercase tracking-wide mb-2">
          {title}
        </h2>
        <div className="h-[3px] w-16 bg-[#4a90c4] rounded-full mb-8" />
      </ScrollReveal>
      <ScrollReveal delay={200}>
        <div style={{ lineHeight: 2.5 }}>
          {sorted.map((r) => (
            <a
              key={r.id}
              href={`/eventi/relatore/${r.slug}`}
              style={{
                fontSize: `${10 + r.eventCount * 2}px`,
                color: hashColor(r.slug),
                display: "inline-block",
                margin: "0 5px 10px 5px",
                textDecoration: "none",
              }}
            >
              {r.nome}{" "}
              <span style={{ fontSize: "0.8em", opacity: 0.6 }}>
                ({r.eventCount})
              </span>
            </a>
          ))}
        </div>
      </ScrollReveal>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  VARIANTE COMPATTA
 *  - card con gradiente chiaro, ring e ombra
 *  - header con eyebrow + titolo + counter relatori + underline
 *  - tag cloud in scroll verticale lento (come AbbiamoIncontratoCompact
 *    della sidebar, ma con cornice grafica più elegante)
 *  - fade top/bottom e pausa al hover
 * ════════════════════════════════════════════════════════════════════════ */
function SpeakersCloudCompact({
  title,
  sorted,
  maxHeight,
  showArchiveLink,
  archiveLinkUrl,
  className,
}: {
  title: string;
  sorted: RelatoreWithCountDto[];
  maxHeight: string;
  showArchiveLink: boolean;
  archiveLinkUrl: string;
  className?: string;
}) {
  const [paused, setPaused] = useState(false);

  // Velocità dello scroll proporzionale al numero di relatori
  // (come AbbiamoIncontratoCompact: min 120 s, ~1.5 s/relatore).
  const effectiveMs = Math.max(120000, sorted.length * 1500);

  return (
    <section className={`max-w-7xl mx-auto px-4 sm:px-8 py-12 ${className || ""}`}>
      <ScrollReveal>
        <div className="relative">
          {/* accenti decorativi sfocati dietro la card */}
          <div className="absolute -top-6 -left-6 w-40 h-40 bg-[#4a90c4]/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#1a3a5c]/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

          {/* Card principale */}
          <div className="relative bg-gradient-to-br from-white via-white to-[#eff6ff] rounded-2xl shadow-xl ring-1 ring-neutral-200/70 overflow-hidden">
            {/* Header */}
            <div className="px-6 sm:px-8 pt-6 sm:pt-7 pb-4 border-b border-neutral-100">
              <h2 className="text-xl sm:text-2xl font-bold text-[#1a3a5c] leading-tight">
                {title}
              </h2>
              <div className="mt-2 h-[2px] w-12 bg-[#4a90c4] rounded-full" />
            </div>

            {/* Contenitore scrollante */}
            <div
              className="relative overflow-hidden px-6 sm:px-8 py-4"
              style={{ maxHeight }}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <div
                style={{
                  lineHeight: 2.3,
                  animation: `speakers-vscroll ${effectiveMs}ms linear infinite`,
                  animationPlayState: paused ? "paused" : "running",
                }}
              >
                {[...sorted, ...sorted].map((r, i) => (
                  <a
                    key={`${r.id}-${i}`}
                    href={`/eventi/relatore/${r.slug}`}
                    style={{
                      fontSize: `${Math.min(11 + r.eventCount * 1.5, 22)}px`,
                      color: hashColor(r.slug),
                    }}
                    className="inline-block mx-1 my-0.5 px-2 py-0.5 rounded-md hover:bg-white hover:shadow-sm transition-all no-underline"
                  >
                    <span className="font-medium">{r.nome}</span>
                    <span className="ml-1 text-[0.75em] opacity-60 tabular-nums">
                      ({r.eventCount})
                    </span>
                  </a>
                ))}
              </div>

              {/* Fade top/bottom per suggerire lo scroll senza nascondere troppo */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white via-white/80 to-transparent" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[#eff6ff] via-[#eff6ff]/80 to-transparent" />
            </div>

            {/* Footer opzionale con link archivio */}
            {showArchiveLink && (
              <div className="px-6 sm:px-8 py-3 border-t border-neutral-100 bg-white/60 flex items-center justify-between text-xs text-neutral-500">
                <span className="italic">Passa il mouse per mettere in pausa</span>
                <a
                  href={archiveLinkUrl}
                  className="inline-flex items-center gap-1 font-semibold text-[#4a90c4] hover:text-[#1a3a5c] transition-colors"
                >
                  Vedi tutti gli eventi
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            )}
          </div>
        </div>
      </ScrollReveal>

      <style jsx>{`
        @keyframes speakers-vscroll {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
      `}</style>
    </section>
  );
}
